import MspLogo from '@/components/decoration/MspLogo';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { useModal } from '@/components/modal/useModal';
import { Action, Card } from '@/components/sections/Card';
import SiBookOpen from '@/icons/SiBookOpen';
import SiDownload from '@/icons/SiDownload';
import SiEdit from '@/icons/SiEdit';
import SiLightBulb from '@/icons/SiLightBulb';
import SiLightOff from '@/icons/SiLightOff';
import SiUsers from '@/icons/SiUsers';
import { cn } from '@/lib/utils';
import { ApprovalStatus } from '@/services/api/contracts/companies';
import { ContractStatus, Partner } from '@/services/api/contracts/partners';
import { NotFoundError } from '@/services/api/errors/APIError';
import { useCompanies } from '@/services/api/hooks/companies';
import { useMspCreditCheck } from '@/services/api/hooks/credit';
import { useDownloadStorageItem, useGetLogo } from '@/services/api/hooks/images';
import { useEnablePartner, useGetPartnerContract, useGetPartnerNda } from '@/services/api/hooks/partners';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import useCSVDownload from '@/services/csv/useCSVDownload';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { deserialiseAddress } from '../onboarding/commonSteps/addressHelper';
import DisableMspModal from './DisableMspModal';
import { ManageBaseCatalogueGlobalMarginModal } from './ManageBaseCatalogueGlobalMarginModal';
import ManageMspUsersModal from './ManageMspUsersModal';
import { usePrintCurrencyString } from '@/utils/price';

interface MspCardProps {
  msp: Partner;
}

const isMspOnboarded = (msp: Partner) => {
  return Boolean(msp.contractStatus == ContractStatus.ContractSigned && Object.keys(msp.registeredInfo).length !== 0);
};

const MspCard: FunctionComponent<MspCardProps> = ({ msp }) => {
  const navigate = useNavigate();
  const role = useRole();

  const disableMspModalProps = useModal<{ mspId: string }>();
  const manageUsersModalProps = useModal<{ mspId: string }>();
  const manageBaseCatalogueGlobalMarginModalProps = useModal<{ mspId: string }>();

  const onMspDisable = () => disableMspModalProps.open({ mspId: msp.id });
  const onMspManageUsers = () => manageUsersModalProps.open({ mspId: msp.id });
  const onMspManageBaseCatalogueGlobalMargin = () => manageBaseCatalogueGlobalMarginModalProps.open({ mspId: msp.id });

  const [customers] = useCompanies(msp.id);

  const [creditCheck] = useMspCreditCheck(msp.id, msp.mspCreditCheckId);

  const { downloadCSV } = useCSVDownload();
  const onMspDownloadCustomers = () => {
    downloadCSV(
      `${msp.name}-pendingCustomers`,
      [
        'RegName',
        'RegAddress1',
        'RegAddress2',
        'RegAddressCity',
        'RegPostCode',
        'RegNumber',
        'NumEmployees',
        'RegVatNumber'
      ],
      customers
        .filter(customer => customer.approvalStatus === ApprovalStatus.Pending)
        .map(customer => {
          const address = deserialiseAddress(customer.registeredInfo.address);
          return {
            RegName: customer.registeredInfo.name,
            RegAddress1: address?.addressLine1,
            RegAddress2: address?.addressLine2 ?? '',
            RegAddressCity: address?.townCity,
            RegPostCode: address?.postalCode,
            RegNumber: customer.registeredInfo.number,
            NumEmployees: customer.registeredInfo.employeeCount,
            RegVatNumber: customer.registeredInfo.vatNumber
          };
        })
    );
  };

  const getContract = useGetPartnerContract();
  const getNda = useGetPartnerNda();
  const downloadStorageItem = useDownloadStorageItem();
  const downloadContract = async () => {
    try {
      const contract = await getContract.mutateAsync({ id: msp.id });
      const url = await downloadStorageItem.mutateAsync({ path: contract.signedDocumentPath });
      window.open(url, '_blank');
    } catch (e) {
      if (e instanceof NotFoundError) {
        toast.error('The contract could not be found');
        return;
      }

      toast.error('The contract could not be downloaded');
    }
  };
  const downloadNda = async () => {
    try {
      const nda = await getNda.mutateAsync({ id: msp.id });
      const url = await downloadStorageItem.mutateAsync({ path: nda.signedDocumentPath });
      window.open(url, '_blank');
    } catch (e) {
      if (e instanceof NotFoundError) {
        toast.error('The NDA could not be found');
        return;
      }

      toast.error('The NDA could not be downloaded');
    }
  };

  const enablePartner = useEnablePartner();
  const onEnable = async () => {
    await enablePartner.mutateAsync({
      id: msp.id
    });
    toast.success(`${msp.name} enabled`);
  };

  const [logoUrl] = useGetLogo(msp.id);

  const printCurrencyString = usePrintCurrencyString({ regionCode: msp.region?.code, forceSpecifyRegion: true });

  return (
    <>
      <DisableMspModal {...disableMspModalProps} />
      <ManageMspUsersModal {...manageUsersModalProps} />
      <ManageBaseCatalogueGlobalMarginModal {...manageBaseCatalogueGlobalMarginModalProps} />
      <Card className="h-fit">
        {logoUrl && (
          <div className="border-b border-secondary-std w-full py-4 flex justify-center">
            <MspLogo id={msp.id} className="w-44 h-[35px]" />
          </div>
        )}
        <div className={cn('flex flex-col p-4', msp.disabled && 'opacity-60')}>
          <div className="flex space-x-2 justify-between">
            <h4 className="font-bold text-2xl flex-1">{msp.name}</h4>
            <Button variant={ButtonVariant.Ghost} onClick={() => navigate(`${msp.id}/edit`)}>
              <SiEdit className="text-xl" />
            </Button>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Region</span>
            <span className="text-primary-600 font-semibold">
              {msp.region?.name || '-'}
            </span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>NDA</span>
            <span className="text-primary-600 font-semibold">
              {(() => {
                switch (msp.contractStatus) {
                  case ContractStatus.Applied:
                    return 'Applied';
                  case ContractStatus.ContractSigned:
                  case ContractStatus.NdaSigned:
                    return 'Signed';
                  default:
                    return 'Unsigned';
                }
              })()}
            </span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Terms & Conditions</span>
            <span className="text-primary-600 font-semibold">
              {(() => {
                switch (msp.contractStatus) {
                  case ContractStatus.Applied:
                  case ContractStatus.NdaSigned:
                    return 'Applied';
                  case ContractStatus.ContractSigned:
                    return 'Signed';
                  default:
                    return 'Unsigned';
                }
              })()}
            </span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Onboarded</span>
            <span className="text-primary-600 font-semibold">{isMspOnboarded(msp) ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex justify-between gap-1 text-sm font-medium">
            <span>Credit Limit</span>
            <span className="text-primary-600 font-semibold">
              {creditCheck !== null
                ? printCurrencyString(creditCheck.creditLimit * 100)
                : '-'}
            </span>
          </div>
        </div>
        <div className="flex flex-col">
          <Action
            onClick={() => navigate(`/msps/${msp.id}/companies/companiesList`)}
            className="flex items-center justify-center gap-1"
          >
            <SiUsers className="text-xl" />
            <span>Customers</span>
          </Action>
          <Action
            onClick={() => navigate(`/msps/${msp.id}/mspCatalogue`)}
            className="flex items-center justify-center gap-1"
          >
            <SiBookOpen className="text-xl" />
            <span>Catalogue</span>
          </Action>
          <Action onClick={onMspManageUsers} disabled={msp.disabled} className="flex items-center justify-center gap-1">
            <SiEdit className="text-xl" />
            <span>Manage users</span>
          </Action>
          <Action
            onClick={onMspManageBaseCatalogueGlobalMargin}
            disabled={msp.disabled}
            className="flex items-center justify-center gap-1"
          >
            <SiEdit className="text-xl" />
            <span>Edit global margin</span>
          </Action>
          {role.type === RoleType.Admin && (
            <Action
              onClick={onMspDownloadCustomers}
              disabled={msp.disabled}
              className="flex items-center justify-center gap-1"
            >
              <SiDownload className="text-xl" />
              <span>Download pending customers</span>
            </Action>
          )}
          {msp.contractStatus >= ContractStatus.NdaSigned && (
            <Action onClick={downloadNda} className="flex items-center justify-center gap-1">
              <SiDownload className="text-xl" />
              <span>Download NDA</span>
            </Action>
          )}
          {msp.contractStatus == ContractStatus.ContractSigned && (
            <Action onClick={downloadContract} className="flex items-center justify-center gap-1">
              <SiDownload className="text-xl" />
              <span>Download contract</span>
            </Action>
          )}
          {!msp.disabled && (
            <Action onClick={onMspDisable} className="flex items-center justify-center gap-1 text-red-600">
              <SiLightOff className="text-xl" />
              <span>Disable</span>
            </Action>
          )}
          {msp.disabled && (
            <Action
              loading={enablePartner.isLoading}
              onClick={onEnable}
              className="flex items-center justify-center gap-1 text-green-600"
            >
              <SiLightBulb className="text-xl" />
              <span>Enable</span>
            </Action>
          )}
        </div>
      </Card>
    </>
  );
};

export default MspCard;
